import { showToast } from './toast';

/**
 * Announce a successful submission on the GTM dataLayer.
 *
 * Google Tag Manager owns the vendor tags (GA4, Meta, Pinterest), so a form
 * only reports that it converted — which tags fire is configured in the
 * container. Orders carry an `ecommerce` payload built server-side, because
 * prices are resolved there and the browser cannot compute them.
 */
function pushDataLayerEvent(event, eventData, response) {
  if (!event) {
    return;
  }

  const ecommerce = response.data?.purchase;

  window.dataLayer = window.dataLayer || [];

  if (ecommerce) {
    // GA4 asks that the previous ecommerce object is cleared first, so values
    // from an earlier event cannot leak into this one.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ...eventData, ecommerce });

    return;
  }

  window.dataLayer.push({ event, ...eventData });
}

/**
 * Submit a form as JSON, handling loading state, network errors, and the
 * standard `{ success, data: { message } }` response shape used by our REST
 * endpoints.
 *
 * Behavior:
 * - Adds `.loading` to the form and `aria-pressed=true` to the submit button while in flight
 * - On success: shows the response message as a persistent toast and invokes `onSuccess`
 * - On non-success response: shows the response message as a 7.5s toast
 * - On network error: shows a generic error toast and invokes `onError`
 *
 * `extraFields` are appended to the FormData before submission — useful for
 * state held outside of form inputs (e.g. selected product/sample IDs).
 * `extraJson` is merged into the JSON body as-is, so values keep their type
 * (arrays/objects survive, unlike FormData's string coercion).
 *
 * `event` names the GTM dataLayer event to push on success (omit for none),
 * with `eventData` as its extra properties.
 *
 * The page's language goes along as `lang`, which Polylang reads to set the
 * request's language, so the confirmation email and messages follow the page.
 */
export function submitForm({
  form,
  url,
  extraFields = {},
  extraJson = {},
  event,
  eventData = {},
  onSuccess,
  onError,
  errorDuration = 7500,
}) {
  const submitButton = form.querySelector('[type="submit"]');

  form.classList.add('loading');
  submitButton?.setAttribute('aria-pressed', 'true');

  const formData = new FormData(form);
  for (const [key, value] of Object.entries(extraFields)) {
    formData.append(key, value);
  }

  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      lang: window.js_localizations.lang,
      ...Object.fromEntries(formData.entries()),
      ...extraJson,
    }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.success) {
        showToast(response.data.message || 'Success', -1);
        pushDataLayerEvent(event, eventData, response);
        onSuccess?.(response);
      } else {
        showToast(response.data.message || 'Error', errorDuration);
      }
    })
    .catch((error) => {
      console.error('Error:', error);
      showToast(window.js_localizations.network_error_message, 5000);
      onError?.(error);
    })
    .finally(() => {
      form.classList.remove('loading');
      submitButton?.setAttribute('aria-pressed', 'false');
    });
}
